import { mapPassionsData } from "../utils/mapPassionsData";
import passionMap from "../pages/OffersLandingPage/generatedPassionMap.json";
import { useEffect, useMemo, useState, useRef } from "react";
import actions from "../config.json";
import { addLeadingZero } from "../utils/addLeadingZero";
import { passionNameToKey } from "../utils/passionNameToKey";
import { ENV } from "../env";
import { generateMarriottReservationUrl } from "../utils/generateMarriottReservationUrl";

export const useOfferDataForProfile = (passionsData) => {
  const passions = useMemo(() => mapPassionsData(passionsData), [passionsData]);
  const filteredPassions = useMemo(
    () =>
      passions.filter(
        (passion) =>
          passion.value !== "false" &&
          passion.value !== "Not Interested" &&
          passion.value !== "N" &&
          passion.value !== "No",
      ),
    [passions],
  );
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const periodRef = useRef(null);

  const ids = useMemo(
    () =>
      filteredPassions.reduce((acc, p) => {
        const key = passionNameToKey(p.name);
        if (passionMap[key]) {
          Array.prototype.push.apply(acc, passionMap[key]);
        }
        return acc;
      }, []),
    [filteredPassions],
  );

  useEffect(() => {
    if (ids?.length) {
      fetchPropertyDetails(ids);
    }
  }, [ids]);

  const fetchPropertyDetails = async (idsToFetch) => {
    setLoading(true);
    setData(null);

    try {
      const actionUrl = ENV.FEATURE_FLAGS.useMbopQueries
        ? "marriott-passions-v3/get-offer-hotels-by-id-mbop"
        : "marriott-passions-v3/get-offer-hotels-by-id";
      const hotelsDetailUrl = actions[actionUrl];

      const today = new Date();
      const tomorrow = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 1,
      );
      const weekAfter = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + 8,
      );
      periodRef.current = [tomorrow, weekAfter];
      const hotelsResponse = await fetch(`${hotelsDetailUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ids: idsToFetch,

          startDate: `${tomorrow.getFullYear()}-${addLeadingZero(tomorrow.getMonth() + 1)}-${addLeadingZero(tomorrow.getDate())}`,
          endDate: `${weekAfter.getFullYear()}-${addLeadingZero(weekAfter.getMonth() + 1)}-${addLeadingZero(weekAfter.getDate())}`,
        }),
      });

      const hotelsData = await hotelsResponse.json();

      const hotelsDataWithCategory = hotelsData.properties
        .map(defineCategory)
        .filter((property) => property !== null);

      setData(hotelsDataWithCategory);
    } catch (error) {
      console.error("Error fetching basic offer data:", error);
    }

    setLoading(false);
  };

  const bookNow = (property) => {
    if (!periodRef.current) {
      return;
    }
    const [startDate, endDate] = periodRef.current;

    const startDateFormatted = `${addLeadingZero(startDate.getMonth() + 1)}/${addLeadingZero(startDate.getDate())}/${startDate.getFullYear()}`;
    const endDateFormatted = `${addLeadingZero(endDate.getMonth() + 1)}/${addLeadingZero(endDate.getDate())}/${endDate.getFullYear()}`;

    const reservationUrl = generateMarriottReservationUrl({
      fromDate: startDateFormatted,
      toDate: endDateFormatted,
      propertyCode: property.id,
    });

    window.open(reservationUrl, "_blank");
  };

  const defineCategory = (property) => {
    // Only consider passion keys that were actually selected by the user
    const usedPassionKeys = filteredPassions
      .map((p) => passionNameToKey(p.name))
      .filter((key) => passionMap[key]);

    const key = usedPassionKeys.find((key) =>
      passionMap[key].includes(property.id),
    );

    // Only return properties that match user's selected passions
    return key ? { ...property, category: key } : null;
  };

  return {
    data,
    loading,
    bookNow,
  };
};
